import { registerLocaleData } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import localeDeDe from '@angular/common/locales/de';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { KfmAppSettings } from '../models/kfm-app.settings';
import { LocalStorageService } from '@shared/storage/local-storage.service';
import { KFM_WEB_SETTINGS } from '@shared/constants/constants';
import { ContactService } from '@modules/contact/services/contact.service';
import { AuthService } from '@shared/authentication/auth.service';
import { Iso3166CountryType } from '@shared/enums/iso-3166-country-type.enum';

registerLocaleData(localeDeDe);

@Injectable({
  providedIn: 'root',
})
export class KaufmannSettingsService {
  private readonly _localstorage = inject(LocalStorageService);
  private readonly _contactService = inject(ContactService);
  private readonly _authService = inject(AuthService);

  private readonly _settingsKey = KFM_WEB_SETTINGS;
  private _canSeeListPrice = true;
  private _tenantCountryType: Iso3166CountryType = Iso3166CountryType.DE;

  private _settings!: KfmAppSettings;
  private _settingsLoaded = false;

  public get appointmentReminder(): boolean {
    return this._settings ? this._settings.appointmentReminder : false;
  }

  public set appointmentReminder(val: boolean) {
    if (this._settings) {
      this._settings.appointmentReminder = val;
      this.saveSettingsToStorage();
    }
  }

  public get autoSync(): boolean {
    return this._settings ? this._settings.autoSync : false;
  }

  public set autoSync(val: boolean) {
    if (this._settings) {
      this._settings.autoSync = val;
      this.saveSettingsToStorage();
    }
  }

  public get logging(): boolean {
    return this._settings ? this._settings.logging : false;
  }

  public set logging(val: boolean) {
    if (this._settings) {
      this._settings.logging = val;
      this.saveSettingsToStorage();
    }
  }

  public get mwst(): number {
    return this._settings ? this._settings.mwst : 0;
  }

  public set mwst(val: number | string) {
    if (this._settings) {
      this._settings.mwst = Number(val);
      this.saveSettingsToStorage();
    }
  }

  public get showAccomplished(): boolean {
    return this._settings ? this._settings.showAccomplished : false;
  }

  public set showAccomplished(val: boolean) {
    if (this._settings) {
      this._settings.showAccomplished = val;
      this.saveSettingsToStorage();
    }
  }

  public get showClosed(): boolean {
    return this._settings ? this._settings.showClosed : false;
  }

  public set showClosed(val: boolean) {
    if (this._settings) {
      this._settings.showClosed = val;
      this.saveSettingsToStorage();
    }
  }

  public get showPrice(): boolean {
    return this._settings
      ? this._settings.showPrice && this._canSeeListPrice
      : false;
  }

  public set showPrice(val: boolean) {
    if (this._settings) {
      this._settings.showPrice = val;
      this.saveSettingsToStorage();
    }
  }

  public get showStockImage(): boolean {
    return this._settings ? this._settings.showStockImage : false;
  }

  public set showStockImage(val: boolean) {
    if (this._settings) {
      this._settings.showStockImage = val;
      this.saveSettingsToStorage();
    }
  }

  public get sendLogfiles(): boolean {
    return this._settings ? this._settings.sendLogfiles : false;
  }

  public set sendLogfiles(val: boolean) {
    if (this._settings) {
      this._settings.sendLogfiles = val;
      this.saveSettingsToStorage();
    }
  }

  public get country(): Iso3166CountryType {
    return this._tenantCountryType;
  }

  public getVatOptions(): string[] {
    if (this._tenantCountryType === Iso3166CountryType.AT) {
      return ['0%', '10%', '20%'];
    }
    return ['0%', '5%', '7%', '16%', '19%'];
  }

  public getDefaultVatRate(): number {
    return (
      this._settings?.mwst ??
      (this._tenantCountryType === Iso3166CountryType.AT ? 20 : 19)
    );
  }

  public async loadSettings(): Promise<void> {
    if (this._settingsLoaded) {
      return;
    }

    this._canSeeListPrice = true;

    await this.getSettings();
    await this._loadTenantCountry();
    this._settingsLoaded = true;
  }

  private async _loadTenantCountry(): Promise<void> {
    try {
      const loginResponse = await firstValueFrom(
        this._authService.isAuthenticated$.pipe(
          filter((response) => response !== undefined),
        ),
      );

      if (!loginResponse?.isAuthenticated) {
        this._tenantCountryType = Iso3166CountryType.DE;
        return;
      }

      const contact = await this._contactService.getTenantContact();
      this._tenantCountryType =
        contact?.addresses?.[0]?.countryType ?? Iso3166CountryType.DE;
    } catch {
      this._tenantCountryType = Iso3166CountryType.DE;
    }
  }

  public async getSettings(): Promise<KfmAppSettings> {
    const settings: string | undefined = await this._localstorage.get(
      this._settingsKey,
    );

    if (settings === undefined || settings.trim() === '') {
      this._settings = {
        appointmentReminder: false,
        autoSync: true,
        logging: false,
        mwst: 19,
        showAccomplished: true,
        showClosed: true,
        showPrice: true,
        sendLogfiles: false,
        showStockImage: true,
      };

      await this._localstorage.set(
        this._settingsKey,
        JSON.stringify(this._settings),
      );
    } else this._settings = JSON.parse(settings);

    return this._settings;
  }

  private async saveSettingsToStorage() {
    try {
      await this._localstorage.set(
        this._settingsKey,
        JSON.stringify(this._settings),
      );
    } catch (reason: unknown) {
      console.log(reason);
    }
  }
}
